import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import StatisticsFilters from './StatisticsFilters';
import StatisticsTable from './StatisticsTable';
import ChartComponent from './ChartComponent';

// Импортируем обновленные функции
import { 
  isErrorCategory, 
  isErrorPercentage,
  getBaseCategoryFromPercentage,
  getPercentageColumnName,
  getQueryParams,
  updateQueryParams,
  ensureDate,
  determinePeriodFromDateRange,
  getTodayDateRange,
  useLocalStorage,
  processErrorCategories,
  formatNumber,
  formatColumnValue
} from './utils';

// Импортируем новые функции для работы с метаданными
import {
  fetchColumnsMetadata,
  getColumnByKey,
  getColumnLabel,
  calculateColumnValue,
  isPercentageColumn,
  getDefaultVisibleColumns,
  getDefaultColumnOrder,
  mergeUserOrderWithMetadata,
  isSystemColumn
} from './columnsAPI';

const StatisticsApp = ({ 
  domains: initialDomainsData, 
  landings: initialLandingData,
  defaultSettings = {
    dateRange: [getTodayDateRange()]
  },
  timePeriods = [
    { value: 'hour', label: '1 hour' },
    { value: 'hour4', label: '4 hours' },
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' }
  ]
}) => {
  const [domains, setDomains] = useState(initialDomainsData);
  const [landings, setLandings] = useState(initialLandingData);
  const [landingOptions, setLandingOptions] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Состояние для метаданных колонок
  const [columnsMetadata, setColumnsMetadata] = useState([]);
  const [isMetadataLoading, setIsMetadataLoading] = useState(true);
  const tableColumnsMetadata = columnsMetadata.filter(col => col.column_type !== 'chart_group');
  
  // Состояние для типа графика
  const [chartType, setChartType] = useState('bar');

  // Используем хук useLocalStorage для visibleColumns и columnOrder
  const [visibleColumns, setVisibleColumns] = useLocalStorage('statisticsVisibleColumns', []);
  const [columnOrder, setColumnOrder] = useLocalStorage('statisticsColumnOrder', []);

  // Обеспечиваем, что defaultSettings.dateRange содержит объекты Date
  const normalizedDefaultSettings = {
    ...defaultSettings,
    dateRange: defaultSettings.dateRange?.map(range => ({
      ...range,
      startDate: ensureDate(range.startDate) || new Date(),
      endDate: ensureDate(range.endDate) || new Date(),
      key: range.key || 'selection'
    })) || [getTodayDateRange()]
  };

  // Установка диапазона дат с приоритетом: URL > defaultSettings > текущий день
  const [selectedDateRange, setSelectedDateRange] = useState(() => {
    const urlParams = getQueryParams();
    
    // Если есть в URL и валидное - используем
    if (urlParams.dateRange && urlParams.dateRange[0] && 
        urlParams.dateRange[0].startDate && urlParams.dateRange[0].endDate) {
      return urlParams.dateRange;
    }
    
    // Если есть в defaultSettings и валидное - используем
    if (normalizedDefaultSettings.dateRange && 
        normalizedDefaultSettings.dateRange[0] && 
        normalizedDefaultSettings.dateRange[0].startDate && 
        normalizedDefaultSettings.dateRange[0].endDate) {
      return normalizedDefaultSettings.dateRange;
    }
    
    // Иначе - текущий день
    return [getTodayDateRange()];
  });

  // Автоматический выбор периода на основе выбранного диапазона дат
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    return determinePeriodFromDateRange(selectedDateRange);
  });



  const [selectedDomain, setSelectedDomain] = useState(() => {
    const urlParams = getQueryParams();
    return urlParams.domain || 'all';
  });

  const [selectedLandings, setSelectedLandings] = useState(() => {
    const urlParams = getQueryParams();
    return urlParams.landings || ['all'];
  });

  const [selectedCountry, setSelectedCountry] = useState(() => {
    const urlParams = getQueryParams();
    return urlParams.country || { value: 'All', label: 'All Countries' };
  });

  const [selectedTrafficSource, setSelectedTrafficSource] = useState(() => {
    const urlParams = getQueryParams();
    return urlParams.trafficSource || { value: 'all', label: 'All Sources' };
  });

  // Вспомогательные функции для получения актуальных значений из localStorage
  const getCurrentVisibleColumns = useCallback(() => {
    try {
      const stored = localStorage.getItem('statisticsVisibleColumns');
      return stored ? JSON.parse(stored) : visibleColumns;
    } catch (error) {
      console.error('Error reading visibleColumns from localStorage:', error);
      return visibleColumns;
    }
  }, [visibleColumns]);

  const getCurrentColumnOrder = useCallback(() => {
    try {
      const stored = localStorage.getItem('statisticsColumnOrder');
      return stored ? JSON.parse(stored) : columnOrder;
    } catch (error) {
      console.error('Error reading columnOrder from localStorage:', error);
      return columnOrder;
    }
  }, [columnOrder]);

  // Загрузка метаданных колонок при инициализации
  useEffect(() => {
    const loadColumnsMetadata = async () => {
      setIsMetadataLoading(true);
      try {
        const metadata = await fetchColumnsMetadata();
        setColumnsMetadata(metadata);
        
        // === ОБРАБОТКА VISIBLE COLUMNS ===
        if (visibleColumns.length === 0) {
          // Первый запуск - используем настройки по умолчанию из метаданных
          const defaultVisible = getDefaultVisibleColumns(metadata);
          setVisibleColumns(defaultVisible);
        } else {
          // Есть пользовательские настройки - фильтруем невалидные
          const validColumns = visibleColumns.filter(colId => {
            if (colId === 'date') return true; // date всегда валидная
            return metadata.some(col => col.key === colId);
          });
          
          if (validColumns.length !== visibleColumns.length) {
            const removedColumns = visibleColumns.filter(colId => !validColumns.includes(colId));
            console.log('Removed invalid columns from visibleColumns:', removedColumns);
            setVisibleColumns(validColumns.length > 0 ? validColumns : getDefaultVisibleColumns(metadata));
          }
        }
        
        // === ОБРАБОТКА COLUMN ORDER (УМНОЕ СЛИЯНИЕ) ===
        if (columnOrder.length === 0) {
          // Первый запуск - используем базовый порядок из метаданных
          const defaultOrder = getDefaultColumnOrder(metadata);
          setColumnOrder(defaultOrder);
        } else {
          // Есть пользовательский порядок - умно мержим с новыми колонками
          const mergedOrder = mergeUserOrderWithMetadata(columnOrder, metadata);
          
          if (JSON.stringify(mergedOrder) !== JSON.stringify(columnOrder)) {
      
            setColumnOrder(mergedOrder);
          }
        }
        
      } catch (error) {
        console.error('Error loading columns metadata:', error);
        setColumnsMetadata([]);
      } finally {
        setIsMetadataLoading(false);
      }
    };

    loadColumnsMetadata();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Обновление периода при изменении диапазона дат
  useEffect(() => {
    const newPeriod = determinePeriodFromDateRange(selectedDateRange);
    setSelectedPeriod(newPeriod);
  }, [selectedDateRange]);

  useEffect(() => {
    if (selectedDomain === 'all') {
      setLandingOptions([{ id: 'all', name: 'All Landings' }]);
    } else {
      const filteredLandings = landings.filter(landing => landing.domain_id === parseInt(selectedDomain));
      setLandingOptions([{ id: 'all', name: 'All Landings' }, ...filteredLandings]);
    }
  }, [selectedDomain, landings]);

  // Обнаружение динамических категорий ошибок из данных
  useEffect(() => {
    if (statistics && statistics.daily_stats && statistics.daily_stats.length > 0 && columnsMetadata.length > 0) {
      // Получаем новые категории ошибок из данных
      const errorCategories = processErrorCategories(statistics.daily_stats, columnsMetadata);
      
      if (errorCategories.length > 0) {
        // Проверяем, есть ли новые категории, которых нет в метаданных
        const newCategories = errorCategories.filter(category => 
          !columnsMetadata.some(col => col.key === category)
        );
        
        if (newCategories.length > 0) {
          console.log('Found new error categories in data:', newCategories);
          // Здесь можно добавить логику для обновления метаданных,
          // но пока просто логируем для информации
        }
        
        // Обновляем columnOrder для новых категорий, если их нет
        let updatedColumnOrder = [...columnOrder];
        let hasChanges = false;
        
        errorCategories.forEach(category => {
          const percentageId = getPercentageColumnName(category);
          
          // Добавляем категорию, если ее еще нет
          if (!updatedColumnOrder.includes(category)) {
            updatedColumnOrder.push(category);
            hasChanges = true;
          }
          
          // Добавляем процент для категории, если его еще нет
          if (!updatedColumnOrder.includes(percentageId)) {
            const categoryIndex = updatedColumnOrder.indexOf(category);
            if (categoryIndex !== -1) {
              updatedColumnOrder.splice(categoryIndex + 1, 0, percentageId);
              hasChanges = true;
            } else {
              updatedColumnOrder.push(percentageId);
              hasChanges = true;
            }
          }
        });
        
        if (hasChanges) {
          setColumnOrder(updatedColumnOrder);
        }
      }
    }
  }, [statistics, columnsMetadata, columnOrder, setColumnOrder]);

  useEffect(() => {
    try {
      const urlParams = getQueryParams();
      let hasUrlParams = false;

      // Применяем параметры из URL только если они явно присутствуют
      if (urlParams.visibleColumns) {
        hasUrlParams = true;
        setVisibleColumns(urlParams.visibleColumns);
      }
      
      if (urlParams.columnOrder) {
        hasUrlParams = true;
        setColumnOrder(urlParams.columnOrder);
      }
      
      // Остальные параметры из URL
      if (urlParams.domain) {
        hasUrlParams = true;
        const domainId = urlParams.domain === 'all' ? 'all' : Number(urlParams.domain);
        setSelectedDomain(domainId);
        if (domainId !== 'all') {
          const filteredLandings = landings.filter(landing => 
            landing.domain_id === domainId
          );
          setLandingOptions([{ id: 'all', name: 'All Landings' }, ...filteredLandings]);
        }
      }
  
      if (urlParams.landings) {
        hasUrlParams = true;
        setSelectedLandings(urlParams.landings);
      }
  
      if (urlParams.country) {
        hasUrlParams = true;
        setSelectedCountry(urlParams.country);
      }

      if (urlParams.trafficSource) {
        hasUrlParams = true;
        setSelectedTrafficSource(urlParams.trafficSource);
      }

      if (hasUrlParams) {
        window.history.replaceState({}, '', window.location.pathname);
      }
  
    } catch (error) {
      console.error('Error parsing params:', error);
    }
  }, [landings, setVisibleColumns, setColumnOrder]);

  // Автоматический запрос данных при загрузке страницы
  useEffect(() => {
    // Запускаем только после загрузки метаданных
    if (!isMetadataLoading) {
      handleGetStatistics();
    }
  }, [isMetadataLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Обработчик для обновления columnOrder и сохранения
  const handleColumnOrderChange = useCallback((newOrder) => {
    setColumnOrder(newOrder);
    
    // Немедленно обновляем URL с новым порядком
    setTimeout(() => {
      const currentVisible = getCurrentVisibleColumns();
      updateQueryParams({
        domain: selectedDomain,
        landings: selectedLandings,
        country: selectedCountry,
        trafficSource: selectedTrafficSource,
        dateRange: selectedDateRange,
        visibleColumns: currentVisible,
        columnOrder: newOrder  // Используем новый порядок
      });
    }, 100); // Небольшая задержка для завершения localStorage обновления
  }, [selectedDomain, selectedLandings, selectedCountry, selectedTrafficSource, selectedDateRange, getCurrentVisibleColumns, setColumnOrder, updateQueryParams]);

  // Обработчик для обновления visibleColumns и сохранения
  const handleVisibleColumnsChange = useCallback((newVisibleColumns) => {
    
    // Убеждаемся, что date всегда присутствует в видимых колонках
    const columnsWithDate = newVisibleColumns.includes('date') 
      ? newVisibleColumns 
      : ['date', ...newVisibleColumns];
    
    setVisibleColumns(columnsWithDate);
    
    // Немедленно обновляем URL с новой видимостью
    setTimeout(() => {
      const currentOrder = getCurrentColumnOrder();
      updateQueryParams({
        domain: selectedDomain,
        landings: selectedLandings,
        country: selectedCountry,
        trafficSource: selectedTrafficSource,
        dateRange: selectedDateRange,
        visibleColumns: columnsWithDate,  // Используем новую видимость
        columnOrder: currentOrder
      });
    }, 100); // Небольшая задержка для завершения localStorage обновления
  }, [selectedDomain, selectedLandings, selectedCountry, selectedTrafficSource, selectedDateRange, getCurrentColumnOrder, setVisibleColumns, updateQueryParams]);

  const getDaysDifference = (dateRange) => {
    const startDate = ensureDate(dateRange[0]?.startDate);
    const endDate = ensureDate(dateRange[0]?.endDate);
    return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  };

  const canUseHourlyData = (dateRange) => {
    return getDaysDifference(dateRange) <= 3;
  };

  const handleGetStatistics = async () => {
    setIsLoading(true);
    try {
      const token = document.querySelector('meta[name="csrf-token"]').content;
      
      // Убедимся, что у нас есть объекты Date
      const startDateObj = ensureDate(selectedDateRange[0]?.startDate);
      const endDateObj = ensureDate(selectedDateRange[0]?.endDate);
      
      if (!startDateObj || !endDateObj) {
        throw new Error('Invalid date range');
      }
      
      // Создаем даты в UTC независимо от локальной временной зоны
      const startDate = new Date(Date.UTC(
        startDateObj.getFullYear(),
        startDateObj.getMonth(),
        startDateObj.getDate(),
        0, 0, 0, 0
      ));
      
      const endDate = new Date(Date.UTC(
        endDateObj.getFullYear(),
        endDateObj.getMonth(),
        endDateObj.getDate(),
        23, 59, 59, 999
      ));
      
      const requestDateRange = [{
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      }];
      
      const requestParams = {
        selectedDomain,
        selectedLandings,
        selectedCountry: selectedCountry.value,
        dateRange: requestDateRange,
        ...(selectedTrafficSource.value !== 'all' && { traffic_source_id: selectedTrafficSource.value })
      };
      
      // Всегда запрашиваем дневную статистику
      const dailyResponse = await fetch('/api/statistics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
        },
        body: JSON.stringify(requestParams),
      });
      
      if (!dailyResponse.ok) {
        throw new Error(`Daily statistics request failed: ${dailyResponse.status}`);
      }
      
      const dailyData = await dailyResponse.json();
      
      // Определяем, можно ли запрашивать почасовую статистику (≤ 3 дней)
      const daysDifference = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      const canUseHourlyData = daysDifference <= 3;
      
      let hourlyStats = [];
      if (canUseHourlyData) {
        try {
          // Запрашиваем почасовую статистику только для периодов ≤ 3 дней
          const hourlyResponse = await fetch('/api/statistics/hourly', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': token,
            },
            body: JSON.stringify(requestParams),
          });
          
          if (hourlyResponse.ok) {
            const hourlyData = await hourlyResponse.json();
            hourlyStats = hourlyData.statistics.hourly_stats || [];
          } else {
            console.warn(`Hourly statistics request failed: ${hourlyResponse.status}`);
            // Не прерываем выполнение, просто оставляем hourlyStats пустым
          }
        } catch (hourlyError) {
          console.warn('Error fetching hourly statistics:', hourlyError);
          // Продолжаем работу без почасовой статистики
        }
      } else {
        console.log(`Skipping hourly statistics request for ${daysDifference} days (> 3 days limit)`);
      }
      
      // Объединяем данные
      setStatistics({
        ...dailyData.statistics,
        hourly_stats: hourlyStats
      });
      
      // Автоматически переключаем период графика если выбран hourly, но данных нет
      if (!canUseHourlyData && (selectedPeriod === 'hour' || selectedPeriod === 'hour4')) {
        setSelectedPeriod('day');
      }
      
    } catch (error) {
      console.error('Error fetching statistics:', error);
      // Можно добавить уведомление пользователю об ошибке
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = () => {
    // Используем вспомогательные функции для получения актуальных значений
    const currentVisibleColumns = getCurrentVisibleColumns();
    const currentColumnOrder = getCurrentColumnOrder();
    
    const link = updateQueryParams({
      domain: selectedDomain,
      landings: selectedLandings,
      country: selectedCountry,
      trafficSource: selectedTrafficSource,
      dateRange: selectedDateRange,
      visibleColumns: currentVisibleColumns,  // Используем актуальные значения
      columnOrder: currentColumnOrder         // Используем актуальные значения
    });
    
    navigator.clipboard.writeText(link);
    setCopySuccess(true);
    setTimeout(() => {
      setCopySuccess(false);
    }, 2000);
  };

  // Конвертация данных в CSV формат (ИСПРАВЛЕННАЯ ВЕРСИЯ)
  const convertToCSV = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0 || columnsMetadata.length === 0) {
      return '';
    }
    
    // Используем вспомогательные функции для получения актуальных значений
    const currentVisibleColumns = getCurrentVisibleColumns();
    const currentColumnOrder = getCurrentColumnOrder();
    
    // Формируем заголовки только на основе видимых колонок
    const headers = currentColumnOrder
      .filter(colId => currentVisibleColumns.includes(colId))
      .map(colId => getColumnLabel(colId, columnsMetadata));

    // Преобразуем данные в формат CSV
    const dataToConvert = data.map(stat => {
      const row = [];
      currentColumnOrder
        .filter(colId => currentVisibleColumns.includes(colId))
        .forEach(colId => {
          const column = getColumnByKey(colId, columnsMetadata);
          
          if (colId === 'date') {
            try {
              const dateObj = typeof stat.date === 'string' ? parseISO(stat.date) : new Date(stat.date);
              row.push(format(dateObj, 'dd.MM.yyyy'));
            } catch (e) {
              console.error('Error formatting date:', stat.date, e);
              row.push('Invalid date');
            }
          } else if (column) {
            // Используем функцию расчета из API
            const value = calculateColumnValue(colId, stat, columnsMetadata);
            if (column.is_percentage) {
              row.push(`${value}%`);
            } else {
              row.push(formatNumber(value));
            }
          } else {
            // Fallback для неизвестных колонок - пробуем напрямую из данных
            const value = stat[colId];
            if (typeof value === 'number') {
              row.push(formatNumber(value));
            } else {
              row.push(value || '');
            }
          }
        });
      return row;
    });

    console.log('CSV data sample row:', dataToConvert[0]);

    // Добавляем строку Average
    const averageRow = [];
    const dataLength = data.length;

    currentColumnOrder
      .filter(colId => currentVisibleColumns.includes(colId))
      .forEach(colId => {
        const column = getColumnByKey(colId, columnsMetadata);
        
        if (colId === 'date') {
          averageRow.push('Average');
        } else if (column) {
          if (column.is_percentage) {
            // ИСПРАВЛЕНИЕ: Для процентных колонок вычисляем общий процент
            const config = column.calculation_config || {};
            let totalNumerator = 0;
            let totalDenominator = 0;
            
            data.forEach(stat => {
              if (column.column_type === 'percentage_ratio') {
                // percentage_ratio
                const numeratorValue = calculateColumnValue(config.numerator, stat, columnsMetadata);
                const denominatorValue = calculateColumnValue(config.denominator, stat, columnsMetadata);
                totalNumerator += numeratorValue;
                totalDenominator += denominatorValue;
              } else if (column.column_type === 'percentage_attempts') {
                // percentage_attempts  
                const targetValue = calculateColumnValue(config.target, stat, columnsMetadata);
                
                // Для total используем config.total_columns
                const totalColumns = config.total_columns || [];
                const statTotal = totalColumns.reduce((sum, col) => {
                  return sum + calculateColumnValue(col, stat, columnsMetadata);
                }, 0);
                
                totalNumerator += targetValue;
                totalDenominator += statTotal;
              }
            });
            
            const avgPercentage = totalDenominator > 0 ? ((totalNumerator / totalDenominator) * 100).toFixed(1) : '0';
            averageRow.push(`${avgPercentage}%`);
          } else {
            // Для обычных колонок вычисляем среднее арифметическое
            const total = data.reduce((sum, stat) => {
              const value = calculateColumnValue(colId, stat, columnsMetadata);
              return sum + (typeof value === 'number' ? value : 0);
            }, 0);
            averageRow.push(formatNumber(Math.round(total / dataLength)));
          }
        } else {
          // Fallback для неизвестных колонок
          const total = data.reduce((sum, stat) => {
            const value = stat[colId];
            return sum + (typeof value === 'number' ? value : 0);
          }, 0);
          averageRow.push(formatNumber(Math.round(total / dataLength)));
        }
      });

    dataToConvert.push(averageRow);

    // Вспомогательная функция для экранирования CSV значений
    const escapeCsvValue = (value) => {
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    // Формируем финальный CSV
    let csv = '';
    
    // Добавляем информацию о фильтрах
    csv += `Traffic Source,${escapeCsvValue(selectedTrafficSource.label || 'All Sources')}\n`;
    csv += `Date Range,${escapeCsvValue(format(ensureDate(selectedDateRange[0]?.startDate) || new Date(), 'dd.MM.yyyy') + ' - ' + format(ensureDate(selectedDateRange[0]?.endDate) || new Date(), 'dd.MM.yyyy'))}\n`;
    csv += `Domain,${escapeCsvValue(selectedDomain === 'all' ? 'All Domains' : domains.find(d => d.id === parseInt(selectedDomain))?.name || 'Unknown')}\n`;
    csv += '\n'; // Пустая строка
    
    // Добавляем заголовки и данные
    csv += headers.map(escapeCsvValue).join(',') + '\n';
    csv += dataToConvert.map(row => row.map(escapeCsvValue).join(',')).join('\n');

    return csv;
  };

  // Вспомогательная функция resolveColumnValue (нужна для CSV)
  const resolveColumnValue = (columnKey, statData, metadata) => {
    // Сначала проверяем, есть ли такое поле напрямую в данных
    if (statData.hasOwnProperty(columnKey)) {
      return statData[columnKey] || 0;
    }
    
    // Если нет, ищем колонку в метаданных и используем её data_source
    const column = getColumnByKey(columnKey, metadata);
    if (column && column.data_source) {
      return statData[column.data_source] || 0;
    }
    
    // Проверяем, может быть columnKey это data_source какой-то колонки
    const columnByDataSource = metadata.find(col => col.data_source === columnKey);
    if (columnByDataSource) {
      // Сначала пробуем найти по ключу колонки
      if (statData.hasOwnProperty(columnByDataSource.key)) {
        return statData[columnByDataSource.key] || 0;
      }
      // Иначе по data_source
      return statData[columnKey] || 0;
    }
    
    // Если ничего не найдено
    return 0;
  };

  // Функция скачивания CSV
  const downloadCSV = () => {
    if (!statistics || !statistics.daily_stats) return;
    
    const csv = convertToCSV(statistics.daily_stats);
    if (!csv) return;
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    try {
      const startDate = ensureDate(selectedDateRange[0]?.startDate);
      const endDate = ensureDate(selectedDateRange[0]?.endDate);
      
      const fileName = `statistics_${startDate ? format(startDate, 'dd-MM-yyyy') : 'start'}_${endDate ? format(endDate, 'dd-MM-yyyy') : 'end'}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading CSV:', error);
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  // Обработчик изменения типа графика
  const handleChartTypeChange = (type) => {
    setChartType(type);
  };

  // Показываем индикатор загрузки метаданных
  if (isMetadataLoading) {
    return (
      <div className="wrapper">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading columns configuration...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="wrapper">
      {/* Фильтры */}
      <StatisticsFilters 
        domains={domains}
        landings={landings}
        setDomains={setDomains}
        setLandings={setLandings}
        selectedDomain={selectedDomain}
        selectedLandings={selectedLandings}
        selectedCountry={selectedCountry}
        selectedDateRange={selectedDateRange}
        selectedTrafficSource={selectedTrafficSource}
        setSelectedDomain={setSelectedDomain}
        setSelectedLandings={setSelectedLandings}
        setSelectedCountry={setSelectedCountry}
        setSelectedDateRange={setSelectedDateRange}
        setSelectedTrafficSource={setSelectedTrafficSource}
        landingOptions={landingOptions}
        setLandingOptions={setLandingOptions}
        visibleColumns={visibleColumns}
        setVisibleColumns={handleVisibleColumnsChange}
        columnOrder={columnOrder}
        isLoading={isLoading}
        handleGetStatistics={handleGetStatistics}
        columnsMetadata={tableColumnsMetadata} // Передаем метаданные
        isMetadataLoading={isMetadataLoading} // Передаем состояние загрузки
        updateQueryParams={updateQueryParams}
        handleCopyLink={handleCopyLink}
        downloadCSV={downloadCSV}
        copySuccess={copySuccess}
      />

      {statistics && (
        <div className="block">
          <div className="mx-auto w-full flex flex-col gap-4">
            {/* График */}
            <div className="w-full">
              <ChartComponent 
                statistics={statistics} 
                selectedPeriod={selectedPeriod}
                onPeriodChange={handlePeriodChange}
                timePeriods={timePeriods}
                columnsMetadata={columnsMetadata} // Передаем метаданные
              />
            </div>

            {/* Таблица статистики */}
            <StatisticsTable 
              statistics={statistics}
              visibleColumns={visibleColumns}
              columnOrder={columnOrder}
              columnsMetadata={tableColumnsMetadata} // Передаем метаданные
              setColumnOrder={handleColumnOrderChange}
              setVisibleColumns={handleVisibleColumnsChange}
              updateQueryParams={updateQueryParams}
              selectedDomain={selectedDomain}
              selectedLandings={selectedLandings}
              selectedCountry={selectedCountry}
              selectedDateRange={selectedDateRange}
              selectedTrafficSource={selectedTrafficSource}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsApp;